import { Button, Card, Datepicker, Progress, WeekStart } from 'flowbite-react';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { HiExclamationCircle } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { QueryResult } from '../../components/Alerts.tsx';
import { useBuildingContext } from '../../components/BuildingContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import NotificationBadge from '../../components/NotificationBadge.tsx';
import ProtectedRoute from '../../components/ProtectedRoute.tsx';
import RequiresBuilding from '../../components/RequiresBuilding.tsx';
import { toastError } from '../../components/Toast.tsx';
import UserAvatar, { UserLike } from '../../components/UserAvatar.tsx';
import { useAppDispatch, useAppSelector } from '../../lib/hooks.ts';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import {
    RoomWithUsers,
    useAfmeldenMutation,
    useBlockRoomMutation,
    useUnblockRoomMutation
} from '../../lib/model/kantoordagApi.ts';
import { useGetBuildingViewQuery } from '../../lib/model/kantoordag/kantoordagBuildingViewStreamingApi.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';
import { ROOM__NO_ROOM } from "../Constants.tsx";

export default function Building() {
    return (
        <RequiresBuilding>
            <BuildingInternal/>
        </RequiresBuilding>
    )
}

function BuildingInternal() {
    const dispatch = useAppDispatch()
    const user = useAppSelector(state => state.user.user)
    const date = DateTime.fromISO(useAppSelector(state => state.date.date))
    const { t, i18n } = useTranslation("building");
    const selectedBuilding = useBuildingContext();
    const [showConfirmSignoff, setShowConfirmSignoff] = useState(false);
    const [userToSignOff, setUserToSignOff] = useState<string>("");

    const roomQuery = useGetBuildingViewQuery({
        date: date.toISODate()!,
        building: selectedBuilding.id,
    })
    const [blockRoomApi, blockResult] = useBlockRoomMutation()
    const [unblockRoomApi, unblockResult] = useUnblockRoomMutation()
    const [signOff, signOffResult] = useAfmeldenMutation()

    const blockRoom = useCallback((room: string) => {
        blockRoomApi({
            building: selectedBuilding.name,
            date: date.toISODate()!,
            room: room
        }).unwrap().then(() => {
            toast.success(t("room_blocked"))
        }).catch(() => {
            toastError({ api: blockResult, message: t("block_unblock_failed") })
        })
    }, [blockRoomApi, selectedBuilding, date, t, blockResult])

    const unblockRoom = useCallback((room: string) => {
        unblockRoomApi({
            building: selectedBuilding.name,
            date: date.toISODate()!,
            room: room
        }).unwrap().then(() => {
            toast.success(t("room_unblocked"))
        }).catch(() => {
            toastError({ api: unblockResult, message: t("block_unblock_failed") })
        })
    }, [unblockRoomApi, unblockResult, selectedBuilding, date, t])

    function dateChanged(date: Date | null) {
        if (date) {
            dispatch(selectDate(DateTime.fromJSDate(date)))
        }
    }

    function progressLabel(room: RoomWithUsers) {
        if (room.room == ROOM__NO_ROOM) {
            return `${room.assigned}`
        }

        return room.assigned + "/" + room.totalSeats;
    }

    function noClicked() {
        setShowConfirmSignoff(false)
    }

    function yesClicked() {
        setShowConfirmSignoff(false)
        signOff({
            datum: date.toISODate()!,
            userId: userToSignOff
        }).unwrap().then(() => {
            toast.success(t("signoff_success"))
        }).catch(() => {
            toastError({ api: signOffResult, message: t("signoff_failed") })
        })
    }

    const onDelete = userHasRole(user, ["ADMIN"]) ? (user: UserLike) => {
        setShowConfirmSignoff(true)
        setUserToSignOff(user.userId)
    } : undefined

    return (
        <ProtectedRoute authenticated={true}>
            <ConfirmDialog
                message={t('sure_to_signoff')}
                yes={t('yes')}
                no={t('no')}
                show={showConfirmSignoff}
                onYes={yesClicked}
                onNo={noClicked}
            />
            <Datepicker onChange={dateChanged}
                        language={i18n.language}
                        className={"w-full"}
                        weekStart={WeekStart.Monday}
                        value={date.toJSDate()}/>
            <Card>
                <QueryResult query={roomQuery} message={t("loading_failed")}>
                    {
                        roomQuery.data !== undefined ?
                            roomQuery.data!.rooms.map( room => {
                            return (
                                <Card key={room.room} className={"relative"}>
                                    <h5>{t(room.room)}</h5>
                                    <div className={"relative"}>
                                        <h4>
                                            {t('workspots')}
                                            { room.seatsAvailable! >= 0 ?
                                                (
                                                    "(" + room.seatsAvailable + " " + t('available') + ")"
                                                )
                                                : null}
                                        </h4>
                                    </div>
                                    <Progress
                                        progress={room.capacityPercentage!}
                                        labelText
                                        textLabelPosition={"outside"}
                                        textLabel={progressLabel(room)}
                                        size={"lg"}
                                    />
                                    <NotificationBadge>{room.assigned}</NotificationBadge>
                                    {
                                        room.blocked ?
                                            <h5>
                                                            <span className={"text-alfa1-brand-primary-500"}>
                                                                <HiExclamationCircle/>
                                                                <Trans t={t}>room_blocked</Trans>
                                                            </span>
                                            </h5>
                                            :
                                            <UserList users={room.users} onDelete={onDelete}/>
                                    }
                                    {
                                        userHasRole(user, "ADMIN") && room.totalSeats >= 0 ?
                                            <div>
                                                {
                                                    room.blocked ?
                                                        <Button onClick={() => unblockRoom(room.room)} color={"primary"}>
                                                            <Trans t={t}>unblock_room</Trans>
                                                        </Button>
                                                        :
                                                        <Button onClick={() => blockRoom(room.room)} color={"primary"}>
                                                            <Trans t={t}>block_room</Trans>
                                                        </Button>
                                                }
                                            </div>
                                            : null
                                    }

                                </Card>
                            )
                        }) : null
                    }
                    {
                        roomQuery.data !== undefined && roomQuery.data.waitlist.length > 0 ?
                            (
                                <Card>
                                    <h5>{t('waitlist')}</h5>
                                    <UserList users={roomQuery.data.waitlist} onDelete={onDelete}/>
                                </Card>
                            ) : null
                    }
                </QueryResult>
            </Card>
        </ProtectedRoute>
    )
}

function UserList({ users, onDelete }: { users: Array<UserLike>, onDelete?: (user: UserLike) => void }) {
    const { t } = useTranslation("building");

    if (users.length == 0) {
        return (
            <span className={"italic"}>{t('noone_found')}</span>
        )
    } else {
        return (
            <div className={"flex flex-row flex-wrap"}>
                {users.map(user =>
                    <UserAvatar user={user} key={user.userName} showCompany={true} showRoom={true} onDelete={onDelete}/>
                )}
            </div>
        )
    }
}